import classNames from 'classnames';
import { ConnectedBackToBackWorkflow } from 'components/common-form/sidePanel/actions/trade/backToBack/BackToBackWorkflow';
import { TradeActionsCustomCheckbox } from 'components/common-form/sidePanel/actions/trade/TradeActionsCustomCheckbox';
import { FormattedMessage } from 'components/shared/Intl';
import { LightAlert } from 'components/shared/LightAlert';
import { TextArea } from 'components/shared/TextField/TextArea';
import { useIsInternalUser } from 'hooks/useIsInternalUser';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { TradeActionsProps } from './tradeConfirmation.connect';
import { connectTradeAction } from './tradeConfirmation.connect';
import { TradeNotifications } from './tradeNotifications';

export const RawTradeConfirmation: FC<TradeActionsProps> = ({
  onFrenchPublicOfferingConfirmed,
  onKidDocumentationConfirmed,
  onTermsheetConfirmed,
  onExecutionOnlyCommentConfirmed,
  onExecutionOnlyDealIdConfirmed,
  onTradeNotificationContactsChanged,
  className,
  haveFrenchPublicOffering,
  haveKidDocumentation,
  quoteId,
  hasConfirmedKidDocumentation,
  hasConfirmedFrenchPublicOffering,
  hasConfirmedTermsheetDisclaimer,

  hadTraded,
  executionOnly,
  isExecutionOnlyAddon,
  comment,
  canComment,
  executionOnlyDealId,

  termsheetButton,
  tabId,
  workspaceType,
}) => {
  const { formatMessage } = useIntl();
  const isInternal = useIsInternalUser();
  const isStaticScreen = useMemo(() => {
    return workspaceType === 'static';
  }, [workspaceType]);

  if (hadTraded === true) {
    return null;
  }

  return (
    <div className={classNames(className)}>
      {executionOnly && (
        <LightAlert severity="info" e2e="exec-only-reminder">
          <FormattedMessage id="form.buttons.ExecOnly.tooltip" />
        </LightAlert>
      )}
      {canComment && (
        <TextArea
          className="form-control d-block"
          placeholder={formatMessage({
            id: executionOnly ? 'form.executionOnly.default' : 'trade.comment.placeholder',
          })}
          e2e="trade-comment"
          value={comment}
          onValueChanged={onExecutionOnlyCommentConfirmed(quoteId)}
          rows={3}
        />
      )}
      {isExecutionOnlyAddon && (
        <TextArea
          className="form-control d-block mt-3"
          placeholder={formatMessage({ id: 'form.executionOnlyDealId.default' })}
          e2e="execution-only-deal-id"
          disabled={!isExecutionOnlyAddon}
          value={executionOnlyDealId}
          onValueChanged={onExecutionOnlyDealIdConfirmed(quoteId)}
          rows={1}
        />
      )}
      {!executionOnly && (
        <>
          <TradeActionsCustomCheckbox
            id="acceptProductTermsheetTermsDisclaimer"
            isChecked={hasConfirmedTermsheetDisclaimer}
            labelId="form.buttons.ConfirmTermsheet"
            onChange={onTermsheetConfirmed(quoteId)}
            trackedEvent="Accepted product termsheet"
          >
            {termsheetButton}
          </TradeActionsCustomCheckbox>
          {haveKidDocumentation && (
            <TradeActionsCustomCheckbox
              id="distributorLegalWarningDisclaimer"
              isChecked={hasConfirmedKidDocumentation}
              labelId="form.buttons.ConfirmLegalWarningForDistributor"
              onChange={onKidDocumentationConfirmed(quoteId)}
              trackedEvent="Accepted kid documentation"
            />
          )}
          {haveFrenchPublicOffering && (
            <TradeActionsCustomCheckbox
              id="francePublicOfferingWarningDisclaimer"
              isChecked={hasConfirmedFrenchPublicOffering}
              labelId="form.buttons.ConfirmLegalWarningForFrancePublicOffering"
              onChange={onFrenchPublicOfferingConfirmed(quoteId)}
              trackedEvent="Accepted french documentation"
            />
          )}
        </>
      )}
      {isInternal && <ConnectedBackToBackWorkflow quoteId={quoteId} formId={tabId} workspace={workspaceType} />}
      {isInternal && isStaticScreen && (
        <TradeNotifications
          tabId={tabId}
          workspaceType={workspaceType}
          onContactIdsChanged={onTradeNotificationContactsChanged(quoteId)}
        />
      )}
    </div>
  );
};

export const TradeConfirmation = connectTradeAction(RawTradeConfirmation);

RawTradeConfirmation.displayName = 'TradeConfirmation';
